import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../api";
import { clearToken } from "../App";
import { toast } from "sonner";
import { Plus, Search, LogOut, Users, Heart, Trash2, Pencil, X } from "lucide-react";
import AddPunchlineDialog from "../components/AddPunchlineDialog";
import PunchlineCard from "../components/PunchlineCard";
import EditPunchlineDialog from "../components/EditPunchlineDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Feed() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [punchlines, setPunchlines] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 280);
    return () => clearTimeout(t);
  }, [search]);

  const loadChildren = async () => {
    const res = await api.get("/children");
    setChildren(res.data);
  };

  const loadPunchlines = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedChildId !== "all") params.child_id = selectedChildId;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const res = await api.get("/punchlines", { params });
      setPunchlines(res.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    loadPunchlines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildId, debouncedSearch]);

  const childMap = useMemo(() => {
    const m = {};
    for (const c of children) m[c.id] = c;
    return m;
  }, [children]);

  const onLike = async (id) => {
    try {
      const res = await api.post(`/punchlines/${id}/like`);
      setPunchlines((prev) => prev.map((p) => (p.id === id ? res.data : p)));
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/punchlines/${id}`);
      setPunchlines((prev) => prev.filter((p) => p.id !== id));
      toast.success("Supprimée");
    } catch (e) {
      toast.error("Erreur de suppression");
    } finally {
      setDeleting(null);
    }
  };

  const onEdited = (updated) => {
    setPunchlines((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  const onCreated = (created) => {
    setPunchlines((prev) => [created, ...prev]);
  };

  const totalByChild = useMemo(() => {
    const m = { all: 0 };
    for (const p of punchlines) {
      m[p.child_id] = (m[p.child_id] || 0) + 1;
    }
    m.all = punchlines.length;
    return m;
  }, [punchlines]);

  const logout = () => {
    clearToken();
    navigate("/login");
  };

  return (
    <div className="min-h-screen paper-bg" data-testid="feed-page">
      {/* Header */}
      <header className="border-b-[1.5px] border-[var(--ink)] bg-[var(--bg)]/85 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-baseline gap-4">
            <h1 className="font-serif-display text-3xl sm:text-4xl leading-none">
              pirkei <span className="text-[var(--kid-levy)]">perla</span>
              <span className="font-ui not-italic text-base sm:text-xl"> &amp; co.</span>
            </h1>
            <span className="hidden md:inline font-hebrew text-2xl text-[var(--ink-soft)]" dir="rtl">פרקי פרלה</span>
            <span className="hidden lg:inline font-ui text-[11px] uppercase tracking-[0.28em] text-[var(--ink-soft)]">
              · {punchlines.length} {punchlines.length > 1 ? "perles" : "perle"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="nav-children-button"
              onClick={() => navigate("/enfants")}
              className="pill flex items-center gap-2"
              title="Enfants"
            >
              <Users size={14} /> Enfants
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="pill"
              title="Déconnexion"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-6">
        <div className="flex flex-col lg:flex-row gap-6 lg:items-end lg:justify-between">
          <div>
            <div className="font-ui text-xs uppercase tracking-[0.28em] text-[var(--ink-soft)] mb-2">
              Fais ton tri
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="filter-tab-all"
                onClick={() => setSelectedChildId("all")}
                className={`pill ${selectedChildId === "all" ? "pill-active" : ""}`}
              >
                tous · {totalByChild.all}
              </button>
              {children.map((c) => (
                <button
                  key={c.id}
                  data-testid={`filter-tab-${c.name.toLowerCase()}`}
                  onClick={() => setSelectedChildId(c.id)}
                  className={`pill ${selectedChildId === c.id ? "pill-active" : ""}`}
                  style={
                    selectedChildId === c.id
                      ? { background: c.color, color: "#1c1b1a", borderColor: "#1c1b1a" }
                      : {}
                  }
                >
                  {c.name.toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"
              />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="chercher un mot…"
                className="pl-9 pr-8 py-2 bg-white border-[1.5px] border-[var(--ink)] rounded-full font-ui text-sm focus:outline-none focus:ring-0 w-64"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-soft)] hover:text-[var(--ink)]"
                  data-testid="search-clear-button"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              data-testid="add-punchline-button"
              onClick={() => setAddOpen(true)}
              className="btn-ink flex items-center gap-2"
            >
              <Plus size={16} /> noter une perle
            </button>
          </div>
        </div>
      </section>

      {/* Feed */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pb-24">
        {loading ? (
          <div className="py-24 text-center font-serif-display text-2xl text-[var(--ink-soft)] italic">
            ouverture du carnet…
          </div>
        ) : punchlines.length === 0 ? (
          <EmptyState onAdd={() => setAddOpen(true)} hasFilter={selectedChildId !== "all" || !!debouncedSearch} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {punchlines.map((p, i) => (
              <div
                key={p.id}
                className="rise"
                style={{ animationDelay: `${Math.min(i * 45, 450)}ms` }}
              >
                <PunchlineCard
                  punch={p}
                  child={childMap[p.child_id]}
                  onLike={() => onLike(p.id)}
                  onDelete={() => setDeleting(p)}
                  onEdit={() => setEditing(p)}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      <AddPunchlineDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        children={children}
        onCreated={onCreated}
      />
      <EditPunchlineDialog
        punch={editing}
        onOpenChange={(v) => !v && setEditing(null)}
        children={children}
        onEdited={onEdited}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent
          data-testid="delete-confirm-dialog"
          className="bg-[var(--bg)] border-[1.5px] border-[var(--ink)] rounded-none shadow-[8px_8px_0_0_#1c1b1a]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-3xl italic text-[var(--ink)]">
              supprimer cette perle ?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-ui text-[var(--ink-soft)]">
              {deleting && (
                <>
                  «{" "}
                  <span className="font-serif-display italic">{deleting.text}</span>{" "}
                  » de{" "}
                  <span className="font-handwrite text-xl text-[var(--ink)]">{deleting.child_name}</span>{" "}
                  sera perdue pour toujours. Tu es sûr ?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel-button"
              className="pill border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white"
            >
              annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={() => deleting && onDelete(deleting.id)}
              className="btn-ink hover:bg-[var(--kid-levy)] hover:text-white"
            >
              oui, supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onAdd, hasFilter }) {
  return (
    <div className="py-24 text-center">
      <div className="font-serif-display text-5xl italic mb-4">
        {hasFilter ? "rien ici…" : "le carnet est vide"}
      </div>
      <p className="font-ui text-[var(--ink-soft)] mb-8">
        {hasFilter
          ? "aucune perle ne correspond à ta recherche."
          : "capture la première perle avant de l'oublier."}
      </p>
      {!hasFilter && (
        <button onClick={onAdd} className="btn-ink inline-flex items-center gap-2">
          <Plus size={16} /> première perle
        </button>
      )}
    </div>
  );
}
